import { atom } from 'jotai';
import { availableRoles } from '@studio/constants/roles';
import { sortByAlphaIgnoreCase } from '@studio/helpers/sortByAlphaIgnoreCase';
import type { CachedFetchLoadInFlight, StoreData } from '@studio/helpers/tsUtils';
import type { Roles } from '@studio/types';
import type API from '@resi-media/api-types';

type User = API.Central.V3.Users.Get.ById.Response;
type Invite = API.Central.V3.UserInvites.Get.List.Response[number];

export type UserRow = User | Invite;

export const isInvite = (row: UserRow): row is Invite => !('userName' in row);

export const usersAtom = atom<StoreData<Readonly<API.Central.V3.Users.Get.List.Response>>>({
  data: [],
  error: undefined,
  hasFetched: false,
  timestamp: 0,
});

export const userInvitesAtom = atom<StoreData<Readonly<API.Central.V3.UserInvites.Get.List.Response>>>({
  data: [],
  error: undefined,
  hasFetched: false,
  timestamp: 0,
});

export const userRolesAtom = atom<StoreData<Readonly<Roles.Get.Role[]>>>({
  data: [],
  error: undefined,
  hasFetched: false,
  timestamp: 0,
});

export const usersLoadInFlightAtom = atom<CachedFetchLoadInFlight | null>(null);

export const userInvitesLoadInFlightAtom = atom<CachedFetchLoadInFlight | null>(null);

export const userRolesLoadInFlightAtom = atom<CachedFetchLoadInFlight | null>(null);

export const usersFilterAtom = atom('');

export const filteredUserRolesAtom = atom<Roles.Get.Role[]>((get) => {
  const { data: roles } = get(userRolesAtom);
  return roles.filter((role) => availableRoles.some((includedRole) => role.id === includedRole));
});

export const filteredUsersAtom = atom<UserRow[]>((get) => {
  const { data: users } = get(usersAtom);
  const { data: invites } = get(userInvitesAtom);
  const filter = get(usersFilterAtom);
  const sortedInvites = invites
    .filter((item) => item.email.toLowerCase().includes(filter.toLowerCase()))
    .sort(sortByAlphaIgnoreCase((invite: Invite) => invite.email));
  const sortedUsers = users
    .filter((item) => item.userName.toLowerCase().includes(filter.toLowerCase()))
    .sort(sortByAlphaIgnoreCase((user: User) => user.userName));
  return [...sortedInvites, ...sortedUsers];
});

export const invalidateUsersCacheAtom = atom(null, (_get, set) => {
  set(usersAtom, (prev) => ({ ...prev, hasFetched: false, timestamp: 0 }));
});

export const invalidateUserInvitesCacheAtom = atom(null, (_get, set) => {
  set(userInvitesAtom, (prev) => ({ ...prev, hasFetched: false, timestamp: 0 }));
});

export const invalidateUserRolesCacheAtom = atom(null, (_get, set) => {
  set(userRolesAtom, (prev) => ({ ...prev, hasFetched: false, timestamp: 0 }));
});
